'use client';

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { logger } from '@/utils/logs/logger';
import {
  CreateTournamentDto,
  Operator,
  ServiceResponse,
  TournamentCategory,
  TournamentSchedule,
  TournamentType,
} from '@alien-platform/bo-core';
import { Button, Stack } from '@mui/material';
import { Box, Grid } from '@mui/system';
import dayjs from 'dayjs';

import { CustomConfigInputProps, InputType } from '@/types/general';
import { CustomConfigInputV2 } from '@/components/core/custom-config-input-v2';
import { LanguageSelector } from '@/components/core/language-selector';
import { SettingsSection } from '@/components/core/settings-section';

export default function NewTournamentContent(): React.JSX.Element | null {
  const searchParams = useSearchParams();
  const selectedOpId = searchParams.get('opId');
  const selectedLanguage = searchParams.get('lang') || 'en';
  const [dto, setDto] = useState<CreateTournamentDto>({
    autoRenew: true,
    description: { en: '' },
    opId: selectedOpId || '',
    schedule: TournamentSchedule.DAILY,
    startTime: dayjs().utc().startOf('day').add(1, 'day').toDate(),
    title: { en: '' },
    type: TournamentType.WAGER_RACE,
    winnersPrizesList: {
      '1': 0,
      '10': 0,
      '2': 0,
      '3': 0,
      '4': 0,
      '5': 0,
      '6': 0,
      '7': 0,
      '8': 0,
      '9': 0,
    },
    selectedGames: [],
    category: TournamentCategory.ALL,
    autoPay: false,
    currency: 'USD',
    coverImage: {},
  });
  const [operator, setOperator] = useState<Operator>();
  const generalFields: CustomConfigInputProps[] = [
    {
      name: 'type',
      label: 'Type',
      required: false,
      type: InputType.SELECT,
      menuItems: Object.keys(TournamentType).map((item) => ({ label: item.replace(/_/g, ' '), value: item })),
      size: { xs: 12, lg: 6 },
      value: dto.type,
    },
    {
      name: 'schedule',
      label: 'Schedule',
      required: true,
      type: InputType.SELECT,
      menuItems: Object.keys(TournamentSchedule).map((item) => ({ label: item.replace(/_/g, ' '), value: item })),
      size: { xs: 12, lg: 6 },
      value: dto.schedule,
    },
    {
      name: 'gameType',
      label: 'Game Type',
      required: true,
      type: InputType.SELECT,
      menuItems: [
        { label: 'Casino', value: 'CASINO' },
        { label: 'Sport', value: 'SPORT' },
      ],
      size: { xs: 12, lg: 6 },
    },
    {
      name: 'currency',
      label: 'Currency',
      required: true,
      type: InputType.SELECT,
      menuItems: operator?.allowedCurrencies?.map((item) => ({ label: item, value: item })) || [],
      size: { xs: 12, lg: 6 },
      value: dto.currency,
    },
    {
      name: 'provider',
      label: 'Proivder',
      type: InputType.SELECT,
      menuItems: [],
      size: { xs: 12, lg: 6 },
    },
    {
      name: 'games',
      label: 'Games',
      required: true,
      type: InputType.SELECT,
      menuItems: [],
      size: { xs: 12, lg: 6 },
      value: dto.selectedGames?.map((item) => item.gameId),
    },
    {
      name: 'kyc',
      label: 'KYC',
      type: InputType.SELECT,
      menuItems: [],
      size: { xs: 12, lg: 6 },
    },
    {
      name: 'tier',
      label: 'Tier',
      type: InputType.SELECT,
      menuItems: [],
      size: { xs: 12, lg: 6 },
    },
    {
      name: 'includeGroups',
      label: 'Include Groups',
      type: InputType.SELECT,
      menuItems: [],
      size: { xs: 12, lg: 6 },
    },
    {
      name: 'excludeGroups',
      label: 'Exclude Groups',
      type: InputType.SELECT,
      menuItems: [],
      size: { xs: 12, lg: 6 },
    },
    {
      name: 'includePlayers',
      label: 'Include Players',
      type: InputType.SELECT,
      menuItems: [],
      size: { xs: 12, lg: 6 },
    },
    {
      name: 'excludePlayers',
      label: 'Exclude Players',
      type: InputType.SELECT,
      menuItems: [],
      size: { xs: 12, lg: 6 },
    },
    {
      name: 'includeBrands',
      label: 'Include Brands',
      type: InputType.SELECT,
      menuItems: [],
      size: { xs: 12, lg: 6 },
    },
    {
      name: 'excludeBrands',
      label: 'Exclude Brands',
      type: InputType.SELECT,
      menuItems: [],
      size: { xs: 12, lg: 6 },
    },

    {
      name: 'autoPay',
      label: 'Auto Pay',
      type: InputType.BOOLEAN,
      size: { xs: 12, lg: 6 },
      direction: 'row',

      value: dto.autoPay,
    },
    {
      name: 'autoRenew',
      label: 'Auto Renew',
      type: InputType.BOOLEAN,
      size: { xs: 12, lg: 6 },
      direction: 'row',

      value: dto.autoRenew,
    },
    {
      name: 'status',
      label: 'Status',
      type: InputType.BOOLEAN,
      size: { xs: 12, lg: 6 },
      direction: 'row',
    },
    {
      name: 'visible',
      label: 'Visible',
      type: InputType.BOOLEAN,
      size: { xs: 12, lg: 6 },
      direction: 'row',
    },
  ];
  const uiFields: CustomConfigInputProps[] = [
    {
      name: 'title',
      label: 'Title',
      required: true,
      type: InputType.INPUT,
      size: { xs: 12 },
      value: dto.title[selectedLanguage],
    },
    {
      name: 'description',
      label: 'Description',
      required: true,
      type: InputType.EDITOR,
      size: { xs: 12 },
      value: dto.description[selectedLanguage],
    },
  ];
  const prizeFields: CustomConfigInputProps[] = [
    {
      label: '1st',
      value: dto.winnersPrizesList[1],
      name: 'winnersPrizesList.1',
      type: InputType.NUMBER,
    },
    {
      label: '2nd',
      value: dto.winnersPrizesList[2],
      name: 'winnersPrizesList.2',
      type: InputType.NUMBER,
    },
    {
      label: '3rd',
      value: dto.winnersPrizesList[3],
      name: 'winnersPrizesList.3',
      type: InputType.NUMBER,
    },
    {
      label: '4th',
      value: dto.winnersPrizesList[4],
      name: 'winnersPrizesList.4',
      type: InputType.NUMBER,
    },
    {
      label: '5th',
      value: dto.winnersPrizesList[5],
      name: 'winnersPrizesList.5',
      type: InputType.NUMBER,
    },
    {
      label: '6th',
      value: dto.winnersPrizesList[6],
      name: 'winnersPrizesList.6',
      type: InputType.NUMBER,
    },
    {
      label: '7th',
      value: dto.winnersPrizesList[7],
      name: 'winnersPrizesList.7',
      type: InputType.NUMBER,
    },
    {
      label: '8th',
      value: dto.winnersPrizesList[8],
      name: 'winnersPrizesList.8',
      type: InputType.NUMBER,
    },
    {
      label: '9th',
      value: dto.winnersPrizesList[9],
      name: 'winnersPrizesList.9',
      type: InputType.NUMBER,
    },
    {
      label: '10th',
      value: dto.winnersPrizesList[10],
      name: 'winnersPrizesList.10',
      type: InputType.NUMBER,
    },
  ];

  const fetchOperator = async (): Promise<void> => {
    try {
      const res = await fetch(`/api/operators/general-config?opId=${selectedOpId}`);
      const result: ServiceResponse<Operator> | undefined = await res.json();
      if (res.ok && result?.success && result.data) {
        setOperator(result.data);
      }
    } catch {
      logger.debug('error');
    }
  };

  useEffect(() => {
    void fetchOperator();
  }, []);

  return (
    <Stack gap={4}>
      <SettingsSection
        title="General Config"
        content={
          <Grid container spacing={4}>
            {generalFields.map((item, index) => (
              <CustomConfigInputV2 key={index} defaultData={dto} setDefaultData={setDto} {...item} />
            ))}
          </Grid>
        }
      />
      <SettingsSection
        title="UI Config"
        content={
          <Stack gap={2}>
            <LanguageSelector />
            <Grid container spacing={4}>
              {uiFields.map((item, index) => (
                <CustomConfigInputV2 key={index} defaultData={dto} setDefaultData={setDto} {...item} />
              ))}
            </Grid>
          </Stack>
        }
      />
      <SettingsSection
        title="Winner Prize List"
        content={
          <Grid container spacing={4}>
            {prizeFields.map((item, index) => (
              <CustomConfigInputV2 key={index} defaultData={dto} setDefaultData={setDto} {...item} />
            ))}
          </Grid>
        }
      />
      <Box sx={{ textAlign: 'end' }}>
        <Button variant="contained" type="submit">
          Save
        </Button>
      </Box>
    </Stack>
  );
}
